import { useState, useId } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Loader2, Plus, Trash2, Copy, ClipboardCheck, Sparkles, RefreshCw,
  MapPin, Mic, Globe, ArrowRight, X, Link2, MessageSquare, GripVertical, Send
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "New Listing", label: "New Listing", color: "#22c55e" },
  { value: "Pre Market", label: "Pre Market", color: "#a855f7" },
  { value: "Under Offer", label: "Under Offer", color: "#f59e0b" },
  { value: "Sold", label: "Sold", color: "#ef4444" },
  { value: "Back on Market", label: "Back on Market", color: "#3b82f6" },
];

const TONE_OPTIONS = [
  { value: "Friendly & upbeat", label: "Friendly & upbeat" },
  { value: "Professional", label: "Professional" },
  { value: "High energy / sales", label: "High energy / sales" },
];

const EMPTY_LISTING = { url: "", status: "New Listing", agent_note: "" };

let _listingId = 0;
const makeListingId = () => `listing-${++_listingId}`;
const makeListingWithId = () => ({ ...EMPTY_LISTING, id: makeListingId() });

// ── Sortable Listing Row ──
function SortableListingRow({ listing, index, total, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: listing.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mu-listing-row ${isDragging ? "mu-dragging" : ""}`}
      data-testid={`mu-listing-${index}`}
    >
      <div className="mu-listing-grip" {...attributes} {...listeners}>
        <GripVertical size={14} />
      </div>
      <div className="mu-listing-num">{index + 1}</div>
      <div className="mu-listing-fields">
        <div className="mu-listing-top">
          <Input
            value={listing.url}
            onChange={(e) => onUpdate(index, "url", e.target.value)}
            placeholder={
              listing.status === "Pre Market"
                ? "Property info — e.g. '4 bedroom family home in Fleet'"
                : "Rightmove URL"
            }
            className="mu-input mu-url-input"
            data-testid={`mu-listing-url-${index}`}
          />
          <Select value={listing.status} onValueChange={(v) => onUpdate(index, "status", v)}>
            <SelectTrigger className="mu-status-select" data-testid={`mu-listing-status-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  <span className="mu-status-dot" style={{ background: s.color }} />
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {listing.status === "Pre Market" && (
          <div className="mu-premarket-hint">No URL needed — just describe the property above</div>
        )}
        <Input
          value={listing.agent_note}
          onChange={(e) => onUpdate(index, "agent_note", e.target.value)}
          placeholder="Agent note (optional) — e.g. '5 viewings in first week, 3 offers received'"
          className="mu-input mu-note-input"
          data-testid={`mu-listing-note-${index}`}
        />
      </div>
      {total > 3 && (
        <button
          className="mu-remove-btn"
          onClick={() => onRemove(index)}
          data-testid={`mu-listing-remove-${index}`}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

export default function MarketUpdateBuilder() {
  const { user } = useAuth();

  const parseBranchName = (raw) => {
    if (!raw) return { agency: "", town: "" };
    const parts = raw.split(" - ");
    let agency = parts[0] || "";
    const town = parts.length > 1 ? parts[parts.length - 1].trim() : "";
    agency = agency.replace(/\s*\([^)]*\)\s*/g, "").trim();
    return { agency, town };
  };
  const parsed = parseBranchName(user?.branch_name);

  // Form state
  const [scriptLength, setScriptLength] = useState("60 seconds");
  const [listings, setListings] = useState([
    makeListingWithId(),
    makeListingWithId(),
    makeListingWithId(),
  ]);
  const [area, setArea] = useState(parsed.town || "");
  const [agentName, setAgentName] = useState(user?.name || "");
  const [branchName, setBranchName] = useState(parsed.agency || "");
  const [tone, setTone] = useState("Friendly & upbeat");
  const [includeSocialCaption, setIncludeSocialCaption] = useState(false);
  const [includeLocalLifestyle, setIncludeLocalLifestyle] = useState(false);

  // Output state
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setListings((prev) => {
      const oldIndex = prev.findIndex((l) => l.id === active.id);
      const newIndex = prev.findIndex((l) => l.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Listing management
  const updateListing = (i, field, value) => {
    setListings((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  };

  const addListing = () => {
    if (listings.length >= 6) {
      toast.error("Maximum 6 listings");
      return;
    }
    setListings((prev) => [...prev, makeListingWithId()]);
  };

  const removeListing = (i) => {
    if (listings.length <= 3) {
      toast.error("Minimum 3 listings required");
      return;
    }
    setListings((prev) => prev.filter((_, idx) => idx !== i));
  };

  // Copy helper
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // Generate
  const handleGenerate = async () => {
    // Validate
    const validListings = listings.filter((l) => l.url.trim());
    if (validListings.length < 3) {
      toast.error("Please fill in at least 3 listings (URL or description for Pre Market)");
      return;
    }
    if (!area.trim()) {
      toast.error("Please enter an area/location");
      return;
    }

    setIsGenerating(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/market-update/generate`, {
        script_length: scriptLength,
        listings: validListings.map((l) => ({
          url: l.url.trim(),
          status: l.status,
          agent_note: l.agent_note?.trim() || "",
        })),
        area: area.trim(),
        agent_name: agentName.trim(),
        branch_name: branchName.trim(),
        tone,
        include_social_caption: includeSocialCaption,
        include_local_lifestyle: includeLocalLifestyle,
      });
      setResult(response.data);
      toast.success("Market update script generated!");

      // Scroll to output on mobile
      if (window.innerWidth < 1024) {
        setTimeout(() => {
          document.getElementById("mu-output")?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to generate script";
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmitScript = async () => {
    if (!result?.script) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/orders/submit`, {
        order_type: "market_update",
        property_title: `Market Update - ${area}`,
        town: area.trim(),
        script: result.script,
        sections: [],
        caption: result.social_caption || null,
        alt_hooks: result.alt_hooks || [],
        cta_variations: result.cta_variations || [],
        listings: listings.filter(l => l.url.trim()).map(l => ({
          url: l.url.trim(),
          status: l.status,
          agent_note: l.agent_note?.trim() || "",
        })),
        script_length: scriptLength,
        tone,
      });
      setSubmitted(true);
      toast.success("Script submitted to production!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit script");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mu-builder" data-testid="market-update-builder">
      <div className="mu-layout">
        {/* ─── Left: Form ─── */}
        <div className="mu-form-col">
          <div className="mu-form-header">
            <Mic size={20} />
            <div>
              <h2>Market Update Script</h2>
              <p>Generate a video script summarising recent property activity</p>
            </div>
          </div>

          {/* Script Length */}
          <div className="mu-field">
            <Label className="mu-label">Script Length</Label>
            <div className="mu-length-toggle" data-testid="mu-length-toggle">
              {["60 seconds", "90 seconds"].map((len) => (
                <button
                  key={len}
                  className={`mu-length-btn ${scriptLength === len ? "active" : ""}`}
                  onClick={() => setScriptLength(len)}
                >
                  {len}
                </button>
              ))}
            </div>
          </div>

          {/* Area */}
          <div className="mu-field">
            <Label className="mu-label">
              <MapPin size={14} /> Area / Location
            </Label>
            <Input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="e.g. Fleet, Cobham, West Byfleet"
              className="mu-input"
              data-testid="mu-area-input"
            />
          </div>

          {/* Agent / Branch */}
          <div className="mu-row-2">
            <div className="mu-field">
              <Label className="mu-label">Agent Name</Label>
              <Input
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Your name"
                className="mu-input"
                data-testid="mu-agent-input"
              />
            </div>
            <div className="mu-field">
              <Label className="mu-label">Branch / Agency</Label>
              <Input
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                placeholder="Agency name"
                className="mu-input"
                data-testid="mu-branch-input"
              />
            </div>
          </div>

          {/* Tone */}
          <div className="mu-field">
            <Label className="mu-label">Tone</Label>
            <div className="mu-tone-toggle" data-testid="mu-tone-toggle">
              {TONE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  className={`mu-tone-btn ${tone === t.value ? "active" : ""}`}
                  onClick={() => setTone(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Listings */}
          <div className="mu-field">
            <Label className="mu-label">
              <Globe size={14} /> Listings ({listings.length}/6)
            </Label>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={listings.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                <div className="mu-listings-list">
                  {listings.map((listing, i) => (
                    <SortableListingRow
                      key={listing.id}
                      listing={listing}
                      index={i}
                      total={listings.length}
                      onUpdate={updateListing}
                      onRemove={removeListing}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {listings.length < 6 && (
              <button className="mu-add-listing-btn" onClick={addListing} data-testid="mu-add-listing">
                <Plus size={14} /> Add Listing
              </button>
            )}
          </div>

          {/* Toggles */}
          <div className="mu-toggles-row">
            <div className="mu-caption-toggle" data-testid="mu-lifestyle-toggle">
              <Checkbox
                id="local-lifestyle"
                checked={includeLocalLifestyle}
                onCheckedChange={setIncludeLocalLifestyle}
              />
              <label htmlFor="local-lifestyle" className="mu-caption-label">
                <MapPin size={14} />
                Include Local Lifestyle Detail
              </label>
            </div>
            <div className="mu-caption-toggle" data-testid="mu-caption-toggle">
              <Checkbox
                id="social-caption"
                checked={includeSocialCaption}
                onCheckedChange={setIncludeSocialCaption}
              />
              <label htmlFor="social-caption" className="mu-caption-label">
                <MessageSquare size={14} />
                Include Social Caption Version
              </label>
            </div>
          </div>

          {/* Generate Button */}
          <Button
            className="mu-generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating}
            data-testid="mu-generate-btn"
          >
            {isGenerating ? (
              <>
                <Loader2 size={16} className="spin" /> Generating...
              </>
            ) : (
              <>
                <Sparkles size={16} /> Generate Market Update Script
              </>
            )}
          </Button>
        </div>

        {/* ─── Right: Output ─── */}
        <div className="mu-output-col" id="mu-output">
          {isGenerating && (
            <div className="mu-generating">
              <Loader2 size={32} className="spin" />
              <h3>Generating your script...</h3>
              <p>Scraping listings & writing your market update</p>
            </div>
          )}

          {!isGenerating && !result && (
            <div className="mu-empty-output">
              <Sparkles size={28} />
              <h3>Your script will appear here</h3>
              <p>Add at least 3 listings, fill in the area, and hit generate</p>
            </div>
          )}

          {result && !isGenerating && (
            <div className="mu-result" data-testid="mu-result">
              {/* Main Script */}
              <div className="mu-result-block">
                <div className="mu-result-header">
                  <h3>
                    <Mic size={16} /> Your Script
                  </h3>
                  <button
                    className="mu-copy-btn"
                    onClick={() => handleCopy(result.script, "script")}
                    data-testid="mu-copy-script"
                  >
                    {copied === "script" ? (
                      <><ClipboardCheck size={14} /> Copied</>
                    ) : (
                      <><Copy size={14} /> Copy Script</>
                    )}
                  </button>
                </div>
                <div className="mu-script-text" data-testid="mu-script-text">
                  {result.script}
                </div>
              </div>

              {/* Alt Hooks */}
              {result.alt_hooks?.length > 0 && (
                <div className="mu-result-block mu-alt-section">
                  <h4>Alternative Hooks</h4>
                  <div className="mu-alt-list">
                    {result.alt_hooks.map((hook, i) => (
                      <div key={i} className="mu-alt-item">
                        <span>{hook}</span>
                        <button
                          className="mu-alt-copy"
                          onClick={() => handleCopy(hook, `hook-${i}`)}
                        >
                          {copied === `hook-${i}` ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Variations */}
              {result.cta_variations?.length > 0 && (
                <div className="mu-result-block mu-alt-section">
                  <h4>CTA Variations</h4>
                  <div className="mu-alt-list">
                    {result.cta_variations.map((cta, i) => (
                      <div key={i} className="mu-alt-item">
                        <span>{cta}</span>
                        <button
                          className="mu-alt-copy"
                          onClick={() => handleCopy(cta, `cta-${i}`)}
                        >
                          {copied === `cta-${i}` ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Caption */}
              {result.social_caption && (
                <div className="mu-result-block mu-caption-block">
                  <div className="mu-result-header">
                    <h4>
                      <MessageSquare size={14} /> Social Caption
                    </h4>
                    <button
                      className="mu-copy-btn small"
                      onClick={() => handleCopy(result.social_caption, "caption")}
                    >
                      {copied === "caption" ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                  <div className="mu-caption-text">{result.social_caption}</div>
                </div>
              )}

              {/* Actions row */}
              <div className="mu-actions-row">
                <button
                  className="mu-regenerate-btn"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  data-testid="mu-regenerate-btn"
                >
                  <RefreshCw size={14} /> Regenerate
                </button>
                <button
                  className={`mu-submit-btn ${submitted ? "mu-submitted" : ""}`}
                  onClick={handleSubmitScript}
                  disabled={isSubmitting || submitted}
                  data-testid="mu-submit-btn"
                >
                  {submitted ? (
                    <><ClipboardCheck size={14} /> Submitted</>
                  ) : isSubmitting ? (
                    <><Loader2 size={14} className="spin" /> Submitting...</>
                  ) : (
                    <><Send size={14} /> Submit Script</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
